// Standings: four divisions, each with the playoff cut line drawn after
// 4th place, plus the tiebreaker columns so it is obvious WHY a player is
// ahead of the person below them.

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Trophy, AlertTriangle, Info, Swords } from 'lucide-react';
import DivisionBadge from './DivisionBadge';
import { DIVISIONS, TIEBREAKER_LABELS, TIEBREAK_METHODS, PLAYOFF_QUALIFIERS_PER_DIVISION } from '../utils/constants';
import { formatHandicap } from '../utils/formatting';

function DecidedBy({ by }) {
  if (!by || by === 'points') return null;
  const styles = {
    h2h: 'bg-sky-100 text-sky-800',
    wins: 'bg-amber-100 text-amber-800',
    winTiePoints: 'bg-violet-100 text-violet-800',
    manual: 'bg-emerald-100 text-emerald-800',
    unresolved: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${styles[by] || 'bg-gray-100 text-gray-700'}`}
          title={`Placed below the player above by: ${TIEBREAKER_LABELS[by]}`}>
      {TIEBREAKER_LABELS[by]}
    </span>
  );
}

function DivisionTable({ division, rows, isAdmin, onResolveTiebreak, tiebreaks }) {
  const cut = PLAYOFF_QUALIFIERS_PER_DIVISION;
  const unresolved = rows.filter(r => r.tiebreakBlocksPlayoff);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <DivisionBadge division={division} />
          <span className="text-sm font-normal text-gray-500">
            top {cut} advance to the playoffs
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {unresolved.length > 0 && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <p className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-4 w-4" />
              Tie for the final playoff spot cannot be broken on paper.
            </p>
            <p className="mt-1">
              Points, head-to-head, wins and win+tie points are all level. Tiebreaker 4 applies:
              a 40 yard dash down #1, or a chip-off on the practice green (chip and make the putt;
              if both convert, a second chip-off decides it on closest chip).
            </p>
            {isAdmin && unresolved.map(r => (
              <div key={r.golfer.id} className="mt-2">
                {r.tiedWith.map(otherId => {
                  const other = rows.find(x => x.golfer.id === otherId);
                  if (!other || other.rank < r.rank) return null;
                  return (
                    <div key={otherId} className="flex flex-wrap items-center gap-2">
                      <span className="text-xs">{r.golfer.name} vs {other.golfer.name}:</span>
                      {TIEBREAK_METHODS.map(m => (
                        <React.Fragment key={m.value}>
                          <Button size="sm" variant="outline"
                            onClick={() => onResolveTiebreak(division, r.golfer, other.golfer, m.value)}>
                            {r.golfer.name} won ({m.label})
                          </Button>
                          <Button size="sm" variant="outline"
                            onClick={() => onResolveTiebreak(division, other.golfer, r.golfer, m.value)}>
                            {other.golfer.name} won ({m.label})
                          </Button>
                        </React.Fragment>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-2 w-8">#</th>
                <th className="py-2 pr-2">Player</th>
                <th className="py-2 pr-2 w-16 text-center">Pts</th>
                <th className="py-2 pr-2 w-12 text-center" title="Wins">W</th>
                <th className="py-2 pr-2 w-12 text-center" title="Ties">T</th>
                <th className="py-2 pr-2 w-12 text-center" title="Losses">L</th>
                <th className="py-2 pr-2 w-16 text-center" title="Forfeits given / received">FF</th>
                <th className="py-2 pr-2 w-20 text-center" title="Tiebreaker 3: points from wins & ties only">W+T pts</th>
                <th className="py-2 pr-2 w-16 text-center">Idx</th>
                <th className="py-2 pr-2">Placed by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <React.Fragment key={r.golfer.id}>
                  <tr className={`border-b last:border-0 ${r.qualifies ? 'bg-emerald-50/40' : ''}`}>
                    <td className="py-2 pr-2 font-semibold text-gray-500">
                      {r.rank}
                      {r.rank === 1 && <Trophy className="ml-1 inline h-3.5 w-3.5 text-amber-500" />}
                    </td>
                    <td className="py-2 pr-2">
                      <span className="font-medium text-gray-900">{r.golfer.name}</span>
                      {r.needsTiebreak && (
                        <Swords className="ml-1.5 inline h-3.5 w-3.5 text-red-500" title="Unbroken tie" />
                      )}
                    </td>
                    <td className="py-2 pr-2 text-center font-bold text-gray-900">{r.points}</td>
                    <td className="py-2 pr-2 text-center">{r.wins}</td>
                    <td className="py-2 pr-2 text-center">{r.ties}</td>
                    <td className="py-2 pr-2 text-center">{r.losses}</td>
                    <td className="py-2 pr-2 text-center text-xs text-gray-500">
                      {r.forfeitsGiven || 0}/{r.forfeitsReceived || 0}
                    </td>
                    <td className="py-2 pr-2 text-center">{r.winTiePoints}</td>
                    <td className="py-2 pr-2 text-center text-gray-500">{formatHandicap(r.golfer.handicap_index)}</td>
                    <td className="py-2 pr-2"><DecidedBy by={r.decidedBy} /></td>
                  </tr>
                  {r.rank === PLAYOFF_QUALIFIERS_PER_DIVISION && i < rows.length - 1 && (
                    <tr>
                      <td colSpan={10} className="p-0">
                        <div className="flex items-center gap-2 py-1">
                          <div className="h-0.5 flex-1 bg-emerald-300" />
                          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                            playoff cut
                          </span>
                          <div className="h-0.5 flex-1 bg-emerald-300" />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Leaderboard({ standings, isAdmin, onResolveTiebreak, tiebreaks }) {
  const [view, setView] = useState('divisions');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {[['divisions', 'By division'], ['overall', 'Overall']].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                view === v ? 'bg-gray-900 text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p><strong>Points:</strong> Win 5 &middot; Tie 3 &middot; Loss 1 &middot; Forfeit 0 (opponent of a forfeit receives 3).</p>
          <p className="mt-1">
            <strong>Tiebreakers:</strong> 1. head-to-head &rarr; 2. most wins &rarr; 3. most points from wins &amp; ties only
            &rarr; 4. 40yd dash / chip-off.
          </p>
          <p className="mt-1 text-gray-500">
            A forfeit win is worth 3 points like a tie, so it does not count as a "win" for tiebreaker 2
            and its points are excluded from tiebreaker 3. Forfeits are shown in the FF column
            (given / received).
          </p>
        </div>
      </div>

      {view === 'divisions' ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {DIVISIONS.map(d => (
            <DivisionTable
              key={d}
              division={d}
              rows={standings.byDivision[d] || []}
              isAdmin={isAdmin}
              onResolveTiebreak={onResolveTiebreak}
              tiebreaks={tiebreaks}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Overall standings</CardTitle>
            <CardDescription>All 32 players by points. Playoff qualification is by division, not overall.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-2 w-8">#</th>
                    <th className="py-2 pr-2">Player</th>
                    <th className="py-2 pr-2 w-28">Division</th>
                    <th className="py-2 pr-2 w-16 text-center">Pts</th>
                    <th className="py-2 pr-2 w-12 text-center">W</th>
                    <th className="py-2 pr-2 w-12 text-center">T</th>
                    <th className="py-2 pr-2 w-12 text-center">L</th>
                    <th className="py-2 pr-2 w-20 text-center">Div rank</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.overall.map(r => (
                    <tr key={r.golfer.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-semibold text-gray-500">{r.overallRank}</td>
                      <td className="py-2 pr-2 font-medium text-gray-900">{r.golfer.name}</td>
                      <td className="py-2 pr-2"><DivisionBadge division={r.golfer.division} /></td>
                      <td className="py-2 pr-2 text-center font-bold">{r.points}</td>
                      <td className="py-2 pr-2 text-center">{r.wins}</td>
                      <td className="py-2 pr-2 text-center">{r.ties}</td>
                      <td className="py-2 pr-2 text-center">{r.losses}</td>
                      <td className="py-2 pr-2 text-center">
                        {r.rank}{r.qualifies && <span className="ml-1 text-emerald-600" title="In a playoff spot">&#10003;</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
